import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge, Banner, BlockStack, Box, Card, InlineStack, Spinner, Text,
} from '@shopify/polaris';
import { useAxiosInstance } from '../../context/AxiosContext';
import { usePromotions, useReorderPromotions, BANNERS } from './api';
import { BannerTile, CardTile, STAR_CSS } from './VskPreview';

const GRID_CSS = `
/* Grid, not wrapping flex: centring a wrapping flex container centres every row, stranding the
   last row's leftover tile. Centring grid tracks moves the block but keeps rows left-aligned.
   "safe" keeps a tile wider than the section reachable. */
.star-arrange-grid { display: grid; grid-template-columns: repeat(auto-fit, 306px); gap: 16px; justify-content: safe center; overflow-x: auto; scroll-snap-type: x proximity; }
.star-arrange-grid--stacked { grid-template-columns: 948px; }
.star-arrange { scroll-snap-align: start; cursor: grab; }
.star-arrange:focus-visible { outline: var(--p-border-width-050) solid var(--p-color-border-focus); outline-offset: 2px; border-radius: var(--p-border-radius-200); }
.star-arrange[data-busy="true"] { cursor: progress; }
.star-arrange[data-dragging="true"] { opacity: 0.4; }
.star-arrange[data-over="true"] { box-shadow: -8px 0 0 -4px var(--p-color-border-emphasis); }
.star-arrange-grid--stacked .star-arrange[data-over="true"] { box-shadow: 0 -8px 0 -4px var(--p-color-border-emphasis); }

/* The track sets the width; the tile only caps the height. */
.star-arrange--card .star-vsk-tile { height: 124px; }
.star-arrange--card .star-vsk-tile > * { height: 100%; box-sizing: border-box; }

.star-arrange--banner .star-partner-card-container { width: 100%; }
`;

const Arrangement = ({ resource, title, appCode, appName }) => {
  const axiosInstance = useAxiosInstance();
  const dragFrom = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const banners = resource === BANNERS;

  const { data: items, isLoading, isError } = usePromotions({
    resource,
    params: { state: 'published' },
    axiosInstance,
  });

  const { mutate: reorder, isLoading: busy } = useReorderPromotions({
    resource,
    axiosInstance,
    onSuccess: () => { setDraft(null); setSaveError(null); },
    onError: () => { setDraft(null); setSaveError('The new order was not saved. The list has been put back.'); },
  });

  const visible = useMemo(() => {
    if (!items) return [];

    return items
      .filter((item) => item.app_codes.includes(appCode))
      .sort((a, b) => (
        (a.app_dashboard[appCode]?.position ?? 0) - (b.app_dashboard[appCode]?.position ?? 0)
      ));
  }, [items, appCode]);

  const ordered = draft
    ? draft.map((id) => visible.find((item) => item.id === id)).filter(Boolean)
    : visible;

  const commit = (ids) => reorder({ appCode, orderedIds: ids });

  const moveTo = (from, to) => {
    if (busy || from === null || from === to || to < 0 || to >= ordered.length) return;

    const ids = ordered.map((item) => item.id);

    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDraft(ids);
    commit(ids);
  };

  // Drag is the only pointer gesture, so arrow keys on a focused tile are the keyboard's way in.
  const onKeyDown = (e, index) => {
    const delta = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 }[e.key];

    if (delta === undefined) return;
    e.preventDefault();
    moveTo(index, index + delta);
  };

  const nameOf = (item) => item.app_name || item.title;

  const body = () => {
    if (isLoading) {
      return (
        <Box padding="500">
          <InlineStack align="center"><Spinner accessibilityLabel={`Loading ${title}`} /></InlineStack>
        </Box>
      );
    }

    if (isError || !items) {
      return (
        <Banner tone="critical" title={`Could not load ${title.toLowerCase()}`}>
          <p>Reload the page to try again.</p>
        </Banner>
      );
    }

    if (ordered.length === 0) {
      return (
        <Box background="bg-surface-secondary" padding="500" borderRadius="200">
          <BlockStack gap="100" inlineAlign="center">
            <Text as="p" fontWeight="medium">Nothing runs in {appName} yet</Text>
            <Text as="p" tone="subdued">Turn this app on from a promotion to place it here.</Text>
          </BlockStack>
        </Box>
      );
    }

    return (
      <Box background="bg-surface-secondary" padding="400" borderRadius="200">
        <div className={`star-vsk star-arrange-grid${banners ? ' star-arrange-grid--stacked' : ''}`}>
          {ordered.map((item, index) => (
            <div
              key={item.id}
              className={`star-arrange star-arrange--${banners ? 'banner' : 'card'}`}
              data-name={nameOf(item)}
              data-busy={busy}
              data-dragging={dragging === index}
              data-over={over === index && dragging !== null && dragging !== index}
              draggable={!busy}
              tabIndex={0}
              role="button"
              aria-label={`${nameOf(item)}, position ${index + 1} of ${ordered.length}. Drag it, or use the arrow keys to move it.`}
              onKeyDown={(e) => {
                if (e.target === e.currentTarget) onKeyDown(e, index);
              }}
              onDragStart={() => { dragFrom.current = index; setDragging(index); }}
              onDragOver={(e) => { e.preventDefault(); setOver(index); }}
              onDragLeave={() => setOver((current) => (current === index ? null : current))}
              onDrop={() => { moveTo(dragFrom.current, index); }}
              onDragEnd={() => { dragFrom.current = null; setDragging(null); setOver(null); }}
            >
              <BlockStack gap="150">
                <InlineStack gap="150" blockAlign="center">
                  <Text as="span" tone="subdued">⠿</Text>
                  <Badge tone="info">{String(index + 1)}</Badge>
                </InlineStack>

                <div className="star-vsk-tile">
                  {banners ? <BannerTile values={item} /> : <CardTile values={item} />}
                </div>
              </BlockStack>
            </div>
          ))}
        </div>
      </Box>
    );
  };

  return (
    <Card>
      <BlockStack gap="300">
        <style>{STAR_CSS + GRID_CSS}</style>

        <InlineStack align="space-between" blockAlign="center" gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">{title}</Text>
            {!isLoading && !isError && <Badge>{String(ordered.length)}</Badge>}
          </InlineStack>

          {busy && <Spinner accessibilityLabel="Saving the new order" size="small" />}
        </InlineStack>

        {saveError && (
          <Banner tone="critical" onDismiss={() => setSaveError(null)}><p>{saveError}</p></Banner>
        )}

        {body()}
      </BlockStack>
    </Card>
  );
};

Arrangement.propTypes = {
  resource: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  appCode: PropTypes.string.isRequired,
  appName: PropTypes.string.isRequired,
};

export default Arrangement;
